import { UserDto } from "../../DTOs/User/UserDto";
import { VIEW_NAMES, ViewKey } from "./pageVocabulary.generated";
import { roleName } from "./roleUtilities";
import { pageOf, RouteRequirements, satisfiesRouteRequirements } from "./routeAccess";

export interface AccessChange {
  role: { name: string | null } | null;
  zones: string[] | null;
  lostPage: ViewKey | null;
}

const sameMembers = <T,>(a: readonly T[], b: readonly T[]) =>
  a.length === b.length && a.every(value => b.includes(value));

const zoneIds = (user: UserDto) => user.zones.map(zone => zone.zoneId);

const contractorZoneIds = (user: UserDto) =>
  user.zones.filter(zone => zone.isSubcontractor).map(zone => zone.zoneId);

const privilegeChanged = (before: UserDto, after: UserDto) =>
  before.isAdmin !== after.isAdmin
  || before.hasLeadAdminRights !== after.hasLeadAdminRights
  || before.isSuperAdmin !== after.isSuperAdmin;

const canOpen = (route: RouteRequirements[], user: UserDto) =>
  route.every(level => satisfiesRouteRequirements(level, user));

export function accessChange(
  before: UserDto | null,
  after: UserDto | null,
  openRoute: RouteRequirements[] = [],
): AccessChange | null {
  if (!before || !after || before.id !== after.id) return null;

  const roleChanged = privilegeChanged(before, after);
  const zonesChanged = !sameMembers(zoneIds(before), zoneIds(after));
  const changed = roleChanged
    || zonesChanged
    || !sameMembers(before.areaKeys, after.areaKeys)
    || !sameMembers(before.visibleTileKeys ?? [], after.visibleTileKeys ?? [])
    || !sameMembers(contractorZoneIds(before), contractorZoneIds(after));
  if (!changed) return null;

  const lostPage = canOpen(openRoute, before) && !canOpen(openRoute, after)
    ? pageOf(openRoute)?.view ?? null
    : null;

  return {
    role: roleChanged ? { name: roleName(after) } : null,
    zones: zonesChanged && !after.isSuperAdmin ? after.zones.map(zone => zone.zone) : null,
    lostPage,
  };
}

const listFormat = new Intl.ListFormat("en-GB", { type: "conjunction" });

function headline({ role, zones }: AccessChange): string {
  const roleClause = role?.name ? `Your role is now ${role.name}` : "Your role has changed";

  if (role && zones) {
    return zones.length
      ? `${roleClause}, with access to ${listFormat.format(zones)}.`
      : `${roleClause}, and you no longer belong to any zone.`;
  }
  if (role) return `${roleClause}.`;
  if (zones) {
    return zones.length ? `You now have access to ${listFormat.format(zones)}.` : "You no longer belong to any zone.";
  }

  return "Your access has been updated.";
}

export function describeAccessChange(change: AccessChange): string {
  return [
    headline(change),
    change.lostPage && `You no longer have access to ${VIEW_NAMES[change.lostPage]}, so you've been taken to the dashboard.`,
    change.role && "You'll be asked to sign in again soon.",
  ].filter(Boolean).join(" ");
}
